import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router";
import { useQuery, useMutation } from "@apollo/client";
import { Check, CreditCard, Mail, AlertTriangle } from "lucide-react";
import { BookingStateBadge, Card, ClientName, clientDisplayName } from "../components/ui";
import {
  GET_SESSION_DETAIL,
  GET_PRIMARY_CUSTOMER,
  CANCEL_BOOKING,
} from "../apollo/sessions";
import { formatShortDate, formatTimeRange, methodToLocation } from "../utils/sessionTime";

export function SessionCancel() {
  const { id: bookingId } = useParams();
  const navigate = useNavigate();

  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [error, setError] = useState<string | null>(null);

  const { data: bookingData, loading: bookingLoading } = useQuery(GET_SESSION_DETAIL, {
    variables: { id: bookingId },
    skip: !bookingId,
  });

  const { data: customerData } = useQuery(GET_PRIMARY_CUSTOMER, {
    variables: { filter: { bookingId, isPrimary: true } },
    skip: !bookingId,
  });

  const [cancelBooking, { loading: submitting }] = useMutation(CANCEL_BOOKING);

  const booking = bookingData?.Booking;
  const primaryCustomer = customerData?.allPeople?.[0] ?? null;

  // Countdown redirect
  useEffect(() => {
    if (!success) return;
    if (countdown <= 0) {
      navigate(`/sessions/${bookingId}`);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [success, countdown, navigate, bookingId]);

  const handleSubmit = async () => {
    setError(null);
    try {
      const result = await cancelBooking({ variables: { bookingId } });
      if (result.data?.cancelBooking) {
        setSuccess(true);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to cancel booking";
      setError(message);
    }
  };

  // Loading
  if (bookingLoading || !booking) {
    return (
      <div className="px-7 pt-10 pb-20">
        <div className="animate-pulse space-y-6">
          <div className="h-4 w-48 rounded bg-canvas" />
          <div className="h-10 w-72 rounded bg-canvas" />
          <div className="h-48 rounded-lg bg-canvas" />
        </div>
      </div>
    );
  }

  // Success
  if (success) {
    return (
      <div className="px-7 pt-10 pb-20 max-w-[720px] mx-auto">
        <div className="bg-paper-hi rounded-lg shadow-card p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-sage mx-auto mb-4 flex items-center justify-center">
            <Check size={24} strokeWidth={2.5} className="text-paper-hi" />
          </div>
          <h2 className="font-display text-[24px] font-medium mb-2">Cancellation initiated</h2>
          <p className="text-[14px] text-ink-3 mb-4">
            The booking has been queued for cancellation. The customer will receive a
            confirmation email shortly.
          </p>
          <p className="text-[12.5px] text-ink-4">
            Redirecting in {countdown} second{countdown !== 1 ? "s" : ""}...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-7 pt-10 pb-20 max-w-[720px] mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-[12.5px] mb-6">
        <Link to="/sessions" className="text-ink-4 hover:text-ink-2">Sessions</Link>
        <span className="text-ink-5">›</span>
        <Link to={`/sessions/${bookingId}`} className="text-ink-4 hover:text-ink-2">
          {primaryCustomer ? clientDisplayName(primaryCustomer) : (booking.primaryCustomerName ?? "Session")}
        </Link>
        <span className="text-ink-5">›</span>
        <span className="text-ink font-medium">Cancel</span>
      </div>

      <h1 className="font-display text-[32px] font-medium leading-[1.1] tracking-tight mb-6">
        Cancel session
      </h1>

      <div className="space-y-4">
        {/* Booking summary */}
        <Card header={{ title: "Booking summary" }}>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {primaryCustomer && (
              <div>
                <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                  Customer
                </div>
                <div className="text-[13px] font-medium">
                  <ClientName customer={primaryCustomer} showSecondary secondaryClassName="text-[11.5px] text-ink-4 font-normal" />
                </div>
                <div className="text-[11.5px] text-ink-4">{primaryCustomer.email}</div>
              </div>
            )}
            {booking.availability && (
              <div>
                <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                  Session time
                </div>
                <div className="text-[13px] font-medium">
                  {formatShortDate(booking.availability.date)}
                </div>
                <div className="text-[11.5px] text-ink-4">
                  {formatTimeRange(
                    booking.availability.date,
                    booking.availability.startTime,
                    booking.availability.endTime,
                  )}
                </div>
              </div>
            )}
            <div>
              <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                Method
              </div>
              <div className="text-[13px] font-medium">{methodToLocation(booking.method)}</div>
            </div>
            <div>
              <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                Status
              </div>
              <BookingStateBadge state={booking.state} />
            </div>
          </div>
        </Card>

        {/* Warning callout */}
        <div className="bg-rust-s/50 rounded-lg border border-rust/20 px-5 py-4 flex items-start gap-3">
          <AlertTriangle size={18} strokeWidth={2} className="text-rust mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-[13.5px] font-medium text-rust">
              This action cannot be undone.
            </div>
            <div className="text-[12.5px] text-ink-3 mt-1">
              This will release any payment authorization and email the customer a
              cancellation confirmation.
            </div>
          </div>
        </div>

        {/* What will happen */}
        <Card header={{ title: "What will happen" }}>
          <div className="divide-hair">
            <div className="px-5 py-3.5 flex items-start gap-3">
              <div className="w-7 h-7 rounded bg-tide-ss flex items-center justify-center text-tide flex-shrink-0 mt-0.5">
                <CreditCard size={13} strokeWidth={2} />
              </div>
              <div>
                <div className="text-[13px] font-medium">Payment authorization released</div>
                <div className="text-[11.5px] text-ink-4">
                  Any held payment authorization will be released immediately. It may take
                  several days for the customer's bank to reflect this.
                </div>
              </div>
            </div>
            <div className="px-5 py-3.5 flex items-start gap-3">
              <div className="w-7 h-7 rounded bg-tide-ss flex items-center justify-center text-tide flex-shrink-0 mt-0.5">
                <Mail size={13} strokeWidth={2} />
              </div>
              <div>
                <div className="text-[13px] font-medium">Customer notified</div>
                <div className="text-[11.5px] text-ink-4">
                  The customer will receive an email confirming the cancellation.
                </div>
              </div>
            </div>
            <div className="px-5 py-3.5 flex items-start gap-3">
              <div className="w-7 h-7 rounded bg-tide-ss flex items-center justify-center text-tide flex-shrink-0 mt-0.5">
                <Check size={13} strokeWidth={2} />
              </div>
              <div>
                <div className="text-[13px] font-medium">Booking status updated</div>
                <div className="text-[11.5px] text-ink-4">
                  The booking will be marked as cancelled and the availability slot will be
                  freed.
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Error */}
        {error && (
          <div className="bg-rust-s rounded-md px-4 py-3 text-[13px] text-rust flex items-start gap-2">
            <span className="font-medium">Error:</span> {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-5 py-2.5 rounded-md bg-rust text-paper-hi text-[13.5px] font-medium hover:bg-rust/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Cancelling..." : "Confirm cancellation"}
          </button>
          <Link
            to={`/sessions/${bookingId}`}
            className="px-5 py-2.5 rounded-md hair border bg-paper-hi text-[13.5px] text-ink-2 hover:bg-paper-2 font-medium"
          >
            Go back
          </Link>
        </div>
      </div>
    </div>
  );
}
